
<template>
  <div class="tab-body">
    <div v-for="(item,index) in tabList" :key="index" class="tab-index">
      <div v-if="commonAuthority[item]" @click="$emit('tabClick',item)"
           :class="['tab-block', {'tab-block-active' : getUserAutherRouter === item && !isOneAuthor}]">
        {{ getTabDisplayText(item, index) }}
        <div :class="['tabUnderline',{'tab-line-active' : getUserAutherRouter === item && !isOneAuthor}]"></div>
      </div>
      <div class="space-block" v-if="commonAuthority[item] && !isOneAuthor"></div>
    </div>
  </div>
</template>

<script>
/*
 * 需求变更说明：
 * - 背景：当前主应用虽已接入国际化，但产品仅国内运营，未配置繁体/英文文案；作为被嵌套的上层应用时，需要跟随子应用语言切换更新头部 Tab 文案。
 * - 方案：读取同域 `localStorage.user_language`（"zh-Hans" | "zh-Hant" | "en"，无值默认 "zh-Hans"），并监听 `storage` 事件以响应子应用更新。
 * - 规则：仅对 `coachPlatform` 进行三语映射：
 *   - zh-Hans（默认）：沿用现有 `$t('coachPlatform')`
 *   - zh-Hant：我的導師
 *   - en：AI Coach
*/
export default {
  name: "HeaderTab",
  components: {},
  props: {
    getUserAutherRouter: {
      type: String,
      default: 'learningCenterAuthority'
    },
    commonAuthority: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      localText: [ //按照tabList的顺序，每个tab对应的国际化语言
        'learningCenter',
        'coachPlatform',
        'applicationTool'
      ],
      tabList: [
        'learningCenterAuthority',
        'coachAuthority',
        'toolsAuthority'
      ],
      isOneAuthor:0,//当前权限数量，只有一个的情况下不展示高亮状态
      /** 子应用同步到 localStorage 的语言标识（"zh-Hans" | "zh-Hant" | "en"） */
      userLanguage: 'zh-Hans',
    }
  },
  watch: {
    commonAuthority: {
      handler(val) {
        let fields = JSON.parse(JSON.stringify(this.tabList));
        this.isOneAuthor = fields.filter(field => val[field]).length === 1;
      },
      deep: true,
      immediate: true
    }
  },
  created() {
  },
  mounted() {
    this.userLanguage = this.getUserLanguageFromLocalStorage();
    window.addEventListener('storage', this.handleStorageChange);
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    /**
     * 获取 localStorage 中的用户语言；无值或异常时默认返回 "zh-Hans"。
     * @returns {"zh-Hans" | "zh-Hant" | "en"}
     */
    getUserLanguageFromLocalStorage() {
      try {
        const rawLang = window.localStorage && window.localStorage.getItem('user_language');
        const lang = this.normalizeUserLanguage(rawLang);
        if (lang === 'zh-Hant' || lang === 'en' || lang === 'zh-Hans') return lang;
        return 'zh-Hans';
      } catch (e) {
        return 'zh-Hans';
      }
    },

    /**
     * 规范化 user_language 的原始值（兼容子应用存储为 JSON.stringify 的情况，如 '"en"'）。
     * @param {string|null} rawLang
     * @returns {string}
     */
    normalizeUserLanguage(rawLang) {
      if (!rawLang) return 'zh-Hans';

      // 兼容：rawLang 可能是 '"zh-Hant"' / '"en"' 这种被 JSON.stringify 过的字符串
      try {
        const parsed = JSON.parse(rawLang);
        if (typeof parsed === 'string') return parsed;
      } catch (e) {
        console.error('规范化 user_language 的原始值失败', e);
      }

      return rawLang;
    },

    /**
     * 子应用变更 localStorage 时触发（同域不同 document 会触发 storage event）。
     * @param {StorageEvent} e
     */
    handleStorageChange(e) {
      if (!e || e.key !== 'user_language') return;
      console.log('监听到子应用的user_language变化事件', e);
      const nextLang = this.getUserLanguageFromLocalStorage();
      console.log('nextLang', nextLang);
      if (nextLang === this.userLanguage) return;
      this.userLanguage = nextLang;
    },

    /**
     * coachPlatform 的三语映射：简体沿用 $t，繁体/英文使用产品固定文案。
     * @param {"zh-Hans" | "zh-Hant" | "en"} lang
     * @returns {string}
     */
    getCoachPlatformText(lang) {
      if (lang === 'zh-Hant') return '我的導師';
      if (lang === 'en') return 'AI Coach';
      return this.$t('coachPlatform');
    },

    /**
     * 获取 Tab 显示文案：默认走国际化，coachPlatform 跟随子应用 user_language。
     * @param {string} item
     * @param {number} index
     * @returns {string}
     */
    getTabDisplayText(item, index) {
      const key = this.localText[index];
      if (key === 'coachPlatform') {
        return this.getCoachPlatformText(this.userLanguage);
      }
      return this.$t(key);
    }
  },
}
</script>

<style scoped lang="scss">
.tab-body {
  user-select: none;
  height: 100%;
  display: flex;
  flex-direction: row;

  .tab-index {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: nowrap;

    .space-block {
      width: var(--width-32);
      height: 100%;
    }
  }

  .tab-block {
    height: 100%;
    position: relative;
    font-size: 18px; /*no*/
    font-weight: var(--font-weight-l);
    color: #445765;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      color: var(--color-blue-deep-hover);
    }
  }

  .tab-block-active {
    font-weight: var(--font-weight-l);
    color: var(--color-blue-active);

    &:hover { /*这里是为了高亮时不展示hover的浅色*/
      color: var(--color-blue-active);
    }
  }

  .tabUnderline {
    width: 100%;
    height: var(--height-2);
    background-color: #FFFFFF;
    position: absolute;
    bottom: 0;
  }

  .tab-line-active {
    background-color: var(--color-blue-active);
  }
}

</style>



// WEBPACK FOOTER //
// src/layout/components/HeaderTab.vue